import { IconBan, IconCheck, IconPencil, IconRotateClockwise, IconTrash, IconX } from "@tabler/icons-react";
import { Group } from "./Group";
import { IconAction } from "./IconAction";

export interface EditRowActionsViewProps {
  mode: "view";
  onEdit: () => void;
  /** Si se define, se usa el ícono de "dar de baja" (IconBan) en vez de "eliminar" (IconTrash) — para
   * entidades con historial referenciado (proveedores, organizaciones, indicadores) que no se borran
   * físicamente. Si además `reactivable` es true, se muestra un tercer botón de reactivar. */
  onDelete?: () => void;
  deleteVariant?: "eliminar" | "baja";
  deleting?: boolean;
  reactivable?: boolean;
  onReactivar?: () => void;
  reactivando?: boolean;
}

export interface EditRowActionsEditProps {
  mode: "edit";
  onSave: () => void;
  onCancel: () => void;
  saving?: boolean;
}

export type EditRowActionsProps = EditRowActionsViewProps | EditRowActionsEditProps;

/**
 * Cluster de acciones compartido por los 6+ `*Row` con toggle ver/editar
 * (`TipoDocumentoRow`, `ProveedorRow`, `OrganizacionRow`, `IndicadorRow`,
 * `EquivalenciaRow`…). A diferencia de intentar generalizar la FILA
 * completa (columnas/campos editables varían demasiado entre entidades —
 * de 1 campo en TipoDocumento a 4 en Proveedor), esta molécula porta solo
 * la parte GENUINAMENTE común: los botones. Cada feature sigue dueña de
 * sus celdas y de sus `useState` por campo.
 *
 * Modo vista: Editar (lápiz) + Eliminar/Dar de baja (basura o prohibido,
 * rojo) [+ Reactivar si aplica]. Modo edición: Guardar (check, filled
 * multiva) + Cancelar (x).
 */
export function EditRowActions(props: EditRowActionsProps) {
  if (props.mode === "edit") {
    return (
      <Group gap="xs" justify="flex-end">
        <IconAction label="Guardar" variant="filled" onClick={props.onSave} loading={props.saving}>
          <IconCheck size={14} />
        </IconAction>
        <IconAction label="Cancelar" variant="subtle" onClick={props.onCancel} disabled={props.saving}>
          <IconX size={14} />
        </IconAction>
      </Group>
    );
  }

  const { onEdit, onDelete, deleteVariant = "eliminar", deleting, reactivable, onReactivar, reactivando } = props;
  return (
    <Group gap="xs" justify="flex-end">
      <IconAction label="Editar" variant="subtle" onClick={onEdit}>
        <IconPencil size={14} />
      </IconAction>
      {reactivable && onReactivar && (
        <IconAction label="Reactivar" variant="subtle" iconColor="exito-600" onClick={onReactivar} loading={reactivando}>
          <IconRotateClockwise size={14} />
        </IconAction>
      )}
      {onDelete && (
        <IconAction
          label={deleteVariant === "baja" ? "Dar de baja" : "Eliminar"}
          variant="subtle"
          iconColor="peligro-600"
          onClick={onDelete}
          loading={deleting}
        >
          {deleteVariant === "baja" ? <IconBan size={14} /> : <IconTrash size={14} />}
        </IconAction>
      )}
    </Group>
  );
}
